// One Orca reply: typed one-line verdict, three lens cards (facts / scenario /
// risks), the Orca data it is based on, and copy / like actions.
import i18n from '@/i18n';
import { Fragment, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useToast } from '@/app/providers/Toast';
import { Icon } from '@/components/ui/Icon';
import { cx } from '@/lib/format';
import { reduceMotion } from '@/lib/random';
import type { Answer } from '@/types';
import { useOpenPosition } from '@/features/portfolio/PositionSheets';
import { useUpgrade } from '@/features/plans/Upgrade';
import { useComparePlans } from '@/features/plans/Compare';
import { PLAN_LABEL, useCaps } from '@/hooks/useCaps';
import type { Feature } from '@/lib/features';
import type { AskCtx } from './contexts';
import { useOrca, type ThreadItem } from './OrcaProvider';

/** the source's icon by where it links (language-independent) */
const SRC_ICON = (l: string, to: string) => (/OrcaWatch|Whale/.test(l) || to.startsWith('/whales') ? 'whale' : /X Radar/.test(l) || to.startsWith('/social') ? 'x' : to.startsWith('/news') ? 'news' : to.startsWith('/calendar') ? 'cal'
  : to.startsWith('/wall-street') ? 'ws' : to.startsWith('/portfolio') ? 'pie' : to.startsWith('/signals') || to.startsWith('/alerts') ? 'link' : to === '/' || to.startsWith('/#') ? 'home' : 'ai');

export const leadOf = (a: Answer) => a.lead || a.known.split(/(?<=\.)\s/)[0];

function AnswerBody({ answer, lead, onLink }: { answer: Answer; lead: string; onLink?: () => void }) {
  const toast = useToast();
  const pos = useOpenPosition();
  const upgrade = useUpgrade();
  const compare = useComparePlans();
  const [vote, setVote] = useState<'' | 'up' | 'down'>('');
  const rootRef = useRef<HTMLDivElement>(null);
  const lens: Array<[string, string, string, string]> = [
    ['known', answer.labels?.[0] ?? i18n.t('ai:lens.known'), 'check', answer.known], ['scen', answer.labels?.[1] ?? i18n.t('ai:lens.scenario'), answer.labels ? 'bell' : 'price', answer.scenario], ['risk', answer.labels?.[2] ?? i18n.t('ai:lens.risk'), answer.labels ? 'card' : 'alert', answer.risk],
  ];
  const copy = () => {
    const a = rootRef.current?.closest('.a') as HTMLElement | null;
    try { void navigator.clipboard?.writeText((a?.innerText ?? '').replace(/\n+/g, '\n')); } catch { /* clipboard unavailable */ }
    toast(i18n.t('ai:copied'));
  };
  const rate = (v: 'up' | 'down') => {
    setVote((cur) => (cur === v ? '' : v));
    toast(v === 'up' ? i18n.t('ai:thanksUp') : i18n.t('ai:thanksDown'));
  };
  return (
    <>
      <p className="ans-lead">{lead}</p>
      <div className="lens" ref={rootRef}>
        {lens.map(([k, l, ic, t], i) => (
          <div key={k} className={cx('lens-c', k)} style={{ '--i': i } as CSSProperties}>
            <span className="lens-k"><Icon name={ic} />{l}</span><p>{t}</p>
          </div>
        ))}
      </div>
      {answer.action?.kind === 'add-position' && (
        <button type="button" className="ans-act" onClick={() => { const a = answer.action as { symbol: string }; onLink?.(); pos.add(a.symbol); }}><Icon name="plus" />{answer.action.label}</button>
      )}
      {answer.action?.kind === 'upgrade' && (
        <button type="button" className="ans-act" onClick={() => { const a = answer.action as { feature: string }; onLink?.(); if (a.feature === 'compare') compare(); else upgrade(a.feature as Feature); }}><Icon name={(answer.action as { feature: string }).feature === 'compare' ? 'link' : 'lock'} />{answer.action.label}</button>
      )}
      {answer.sources && (
        <div className="ans-src">
          <span>{i18n.t('ai:basedOn')}</span>
          {answer.sources.map(([l, to]) => <Link key={l} to={to} onClick={onLink}><Icon name={SRC_ICON(l, to)} />{l}</Link>)}
        </div>
      )}
      <div className="ans-foot">
        <div className="ans-acts">
          <button type="button" aria-label={i18n.t('common:action.copy')} onClick={copy}><Icon name="copy" /></button>
          <button type="button" aria-label={i18n.t('ai:useful')} className={cx(vote === 'up' && 'on')} onClick={() => rate('up')}><Icon name="like" /></button>
          <button type="button" aria-label={i18n.t('ai:notUseful')} className={cx('dn', vote === 'down' && 'on')} onClick={() => rate('down')}><Icon name="like" /></button>
        </div>
        <span>{i18n.t('ai:notAdvice')}</span>
      </div>
    </>
  );
}

const Mark = () => <span className="ans-mark"><img src="/assets/brand/orca-mark.png" alt="" /></span>;

/* ---------- a real Orca AI answer (ORCA API) ---------- */
// The reply is the bot's Telegram HTML (b, i, u, s, code, pre, a, blockquote),
// already sanitized by the server. It is rebuilt here as React elements from an
// allow-list — never injected as HTML — so nothing else can reach the page.
const TAGS: Record<string, string> = { b: 'b', strong: 'b', i: 'i', em: 'i', u: 'u', ins: 'u', s: 's', strike: 's', del: 's', code: 'code', pre: 'pre', blockquote: 'blockquote', a: 'a', br: 'br' };
function lines(text: string, key: string): ReactNode[] {
  return text.split('\n').flatMap((part, i) => (i ? [<br key={`${key}-br${i}`} />, part] : [part]));
}
function toNodes(node: Node, key: string): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) return <Fragment key={key}>{lines(node.textContent ?? '', key)}</Fragment>;
  if (node.nodeType !== Node.ELEMENT_NODE) return null;
  const el = node as Element;
  const kids = Array.from(el.childNodes).map((c, i) => toNodes(c, `${key}.${i}`));
  const tag = TAGS[el.tagName.toLowerCase()];
  if (!tag) return <Fragment key={key}>{kids}</Fragment>;
  if (tag === 'br') return <br key={key} />;
  if (tag === 'a') {
    const href = el.getAttribute('href') ?? '';
    return /^https?:\/\//i.test(href) ? <a key={key} href={href} target="_blank" rel="noopener noreferrer nofollow">{kids}</a> : <Fragment key={key}>{kids}</Fragment>;
  }
  const T = tag as 'b';
  return <T key={key}>{kids}</T>;
}
export function SafeHtml({ html }: { html: string }) {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  return <div className="ans-html">{Array.from(doc.body.childNodes).map((n, i) => toNodes(n, String(i)))}</div>;
}

function RealMessage({ item, chatId, onSettled, onLink }: { item: ThreadItem; chatId: string; onSettled?: () => void; onLink?: () => void }) {
  const { finish, retry, resetAt, limit } = useOrca();
  const caps = useCaps();
  const upgrade = useUpgrade();
  const toast = useToast();
  const [vote, setVote] = useState<'' | 'up' | 'down'>('');
  const rootRef = useRef<HTMLDivElement>(null);
  const settled = useRef(onSettled);
  settled.current = onSettled;
  useEffect(() => { if (item.state !== 'pending') settled.current?.(); }, [item.state]);
  if (item.state === 'pending') {
    return (
      <div className="msg-o thinking"><Mark />
        <div className="a thinking">
          <span className="dots"><i /><i /><i /></span>
          <span className="think-t">{item.ctx ? i18n.t('ai:thinkingCtx', { what: item.ctx.label }) : i18n.t('ai:thinking')}</span>
          <button type="button" className="st-stop" onClick={() => finish(chatId, item.id, 'stopped')} aria-label={i18n.t('ai:stopAria')}><i />{i18n.t('ai:stop')}</button>
        </div>
      </div>
    );
  }
  if (item.state === 'limit') {
    return (
      <div className="msg-o done"><Mark />
        <div className="a done st limit">
          <span className="st-k"><Icon name="alert" />{i18n.t('ai:limit.title')}</span>
          <p className="ans-lead">{i18n.t('ai:limit.used', { count: limit })}</p>
          <p className="st-t">{i18n.t('ai:limit.text', { time: resetAt })}</p>
          <div className="st-acts">
            {caps.plan !== 'premium'
              ? <button type="button" className="st-btn on" onClick={() => { onLink?.(); upgrade('more_ai'); }}>{i18n.t('ai:limit.more', { plan: PLAN_LABEL[caps.plan === 'free' ? 'plus' : 'premium'] })}</button>
              : <Link className="st-btn" to="/profile#billing" onClick={onLink}>{i18n.t('ai:limit.planLink')}</Link>}
          </div>
        </div>
      </div>
    );
  }
  if (item.state === 'error' || item.state === 'stopped') {
    const stoppedNow = item.state === 'stopped';
    return (
      <div className="msg-o done"><Mark />
        <div className={cx('a done st', stoppedNow ? 'stopped' : 'err')}>
          <span className="st-k"><Icon name={stoppedNow ? 'close' : 'alert'} />{stoppedNow ? i18n.t('ai:stopped') : i18n.t('ai:error.title')}</span>
          <p className="st-t">{stoppedNow ? i18n.t('ai:real.stoppedText') : item.error ?? i18n.t('ai:error.text')}</p>
          <div className="st-acts"><button type="button" className={cx('st-btn', !stoppedNow && 'on')} onClick={() => retry(chatId, item.id)}><Icon name="refresh" />{i18n.t('common:action.retry')}</button></div>
        </div>
      </div>
    );
  }
  const copy = () => {
    try { void navigator.clipboard?.writeText((rootRef.current?.innerText ?? '').replace(/\n{3,}/g, '\n\n')); } catch { /* clipboard unavailable */ }
    toast(i18n.t('ai:copied'));
  };
  const rate = (v: 'up' | 'down') => { setVote((cur) => (cur === v ? '' : v)); toast(v === 'up' ? i18n.t('ai:thanksUp') : i18n.t('ai:thanksDown')); };
  return (
    <div className="msg-o done"><Mark />
      <div className="a done real">
        <div ref={rootRef}><SafeHtml html={item.answer.html ?? ''} /></div>
        <div className="ans-foot">
          <div className="ans-acts">
            <button type="button" aria-label={i18n.t('common:action.copy')} onClick={copy}><Icon name="copy" /></button>
            <button type="button" aria-label={i18n.t('ai:useful')} className={cx(vote === 'up' && 'on')} onClick={() => rate('up')}><Icon name="like" /></button>
            <button type="button" aria-label={i18n.t('ai:notUseful')} className={cx('dn', vote === 'down' && 'on')} onClick={() => rate('down')}><Icon name="like" /></button>
          </div>
          <span>{i18n.t('ai:notAdvice')}</span>
        </div>
      </div>
    </div>
  );
}

/** Orca message for one thread item: thinking → typed verdict → cards, or
 *  an error / stopped / limit / unknown-question state with a way forward. */
export function OrcaMessage({ item, chatId, onSettled, onLink }: { item: ThreadItem; chatId: string; onSettled?: () => void; onLink?: () => void }) {
  const { finish, retry, resetAt, limit, gate } = useOrca();
  const caps = useCaps();
  const upgrade = useUpgrade();
  // checked at render: a plan change re-gates the history too
  const answer = gate(item.answer);
  const full = answer.lead === '__unknown__' ? unknownLead() : leadOf(answer);
  const pending = item.state === 'pending';
  const [phase, setPhase] = useState<'thinking' | 'typing' | 'done'>(pending && !reduceMotion() ? 'thinking' : 'done');
  const [typed, setTyped] = useState(phase === 'done' ? full : '');
  const settled = useRef(onSettled);
  settled.current = onSettled;
  // a retried item starts over
  useEffect(() => { if (pending) { setPhase(reduceMotion() ? 'done' : 'thinking'); setTyped(''); } }, [item.attempt]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!pending || phase !== 'thinking') return;
    const t = window.setTimeout(() => { if (item.fail) finish(chatId, item.id, 'error'); else setPhase('typing'); }, item.fail ? 1600 : 1100);
    return () => window.clearTimeout(t);
  }, [phase, pending, item.fail, item.id, chatId, finish]);
  useEffect(() => {
    if (!pending || phase !== 'typing') return;
    let k = 0;
    let t = 0;
    const tick = () => {
      k += 2;
      setTyped(full.slice(0, k));
      if (k < full.length) t = window.setTimeout(tick, 14);
      else { setPhase('done'); finish(chatId, item.id, 'done'); settled.current?.(); }
    };
    tick();
    return () => window.clearTimeout(t);
  }, [phase, pending, full, chatId, item.id, finish]);
  // reduced motion: no animation, finish at once
  useEffect(() => { if (pending && reduceMotion()) finish(chatId, item.id, item.fail ? 'error' : 'done'); }, [pending, item.attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const stop = () => finish(chatId, item.id, 'stopped');
  const again = () => retry(chatId, item.id);

  if (item.state === 'limit') {
    return (
      <div className="msg-o done"><Mark />
        <div className="a done st limit">
          <span className="st-k"><Icon name="alert" />{i18n.t('ai:limit.title')}</span>
          <p className="ans-lead">{i18n.t('ai:limit.used', { count: limit })}</p>
          <p className="st-t">{i18n.t('ai:limit.text', { time: resetAt })}</p>
          <div className="st-acts">
            {caps.plan !== 'premium'
              ? <button type="button" className="st-btn on" onClick={() => { onLink?.(); upgrade('more_ai'); }}>{i18n.t('ai:limit.more', { plan: PLAN_LABEL[caps.plan === 'free' ? 'plus' : 'premium'] })}</button>
              : <Link className="st-btn" to="/profile#billing" onClick={onLink}>{i18n.t('ai:limit.planLink')}</Link>}
          </div>
        </div>
      </div>
    );
  }
  if (item.state === 'error') {
    return (
      <div className="msg-o done"><Mark />
        <div className="a done st err">
          <span className="st-k"><Icon name="alert" />{i18n.t('ai:error.title')}</span>
          <p className="st-t">{i18n.t('ai:error.text')}</p>
          <div className="st-acts"><button type="button" className="st-btn on" onClick={again}><Icon name="refresh" />{i18n.t('common:action.retry')}</button></div>
        </div>
      </div>
    );
  }
  if (item.state === 'stopped') {
    return (
      <div className="msg-o done"><Mark />
        <div className="a done st stopped">
          {typed && <p className="ans-lead dim">{typed}…</p>}
          <span className="st-k"><Icon name="close" />{i18n.t('ai:stopped')}</span>
          <div className="st-acts"><button type="button" className="st-btn" onClick={again}><Icon name="refresh" />{i18n.t('ai:regenerate')}</button></div>
        </div>
      </div>
    );
  }
  if (pending && phase === 'thinking') {
    return (
      <div className="msg-o thinking"><Mark />
        <div className="a thinking">
          <span className="dots"><i /><i /><i /></span>
          <span className="think-t">{item.ctx ? i18n.t('ai:thinkingCtx', { what: item.ctx.label }) : i18n.t('ai:thinking')}</span>
          <button type="button" className="st-stop" onClick={stop} aria-label={i18n.t('ai:stopAria')}><i />{i18n.t('ai:stop')}</button>
        </div>
      </div>
    );
  }
  const unknown = answer.lead === '__unknown__' || item.state === 'unknown';
  if (unknown) {
    return (
      <div className={cx('msg-o', pending ? 'thinking' : 'done')}><Mark />
        <div className={cx('a st unknown', phase === 'done' && 'done')}>
          <p className="ans-lead">{typed}</p>
          {phase === 'done' && <p className="st-t">{i18n.t('ai:demoHint')}</p>}
        </div>
      </div>
    );
  }
  return (
    <div className={cx('msg-o', pending ? 'thinking' : 'done')}><Mark />
      <div className={cx('a', phase === 'done' && 'done')}>
        <AnswerBody answer={answer} lead={typed} onLink={onLink} />
        {pending && phase === 'typing' && <button type="button" className="st-stop inl" onClick={stop}><i />{i18n.t('ai:stop')}</button>}
      </div>
    </div>
  );
}
const unknownLead = () => i18n.t('ai:unknown');

export function WelcomeMessage() {
  return (
    <div className="msg-o done">
      <Mark />
      <div className="a done">
        <p className="ans-lead">{i18n.t('ai:welcome.title')}</p>
        <p className="welcome-t">{i18n.t('ai:welcome.text')}</p>
      </div>
    </div>
  );
}

/** Question bubble (+ its context chip) and Orca's reply, for each item. */
export function Thread({ chatId, items, onSettled, onLink }: { chatId: string; items: ThreadItem[]; onSettled?: () => void; onLink?: () => void }) {
  return (
    <>
      {items.map((m) => (
        <div className="pair" key={m.id}>
          {m.ctx && <span className="q-ctx"><Icon name={m.ctx.icon} />{m.ctx.label}</span>}
          <div className="q">{m.q}</div>
          {m.real ? <RealMessage item={m} chatId={chatId} onSettled={onSettled} onLink={onLink} /> : <OrcaMessage item={m} chatId={chatId} onSettled={onSettled} onLink={onLink} />}
        </div>
      ))}
    </>
  );
}

/** The one «Спросить Orca» button used everywhere (pill, or round icon in tight rows). */
export function AskOrca({ ctx, variant = 'pill', label = i18n.t('ai:ask') }: { ctx: AskCtx; variant?: 'pill' | 'icon' | 'ghost'; label?: string }) {
  const { askAbout } = useOrca();
  return (
    <button type="button" className={cx('askb', variant)} title={variant === 'icon' ? `${label}: ${ctx.question}` : undefined} aria-label={`${label}: ${ctx.label}`}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); askAbout(ctx); }}>
      <span className="askb-m" aria-hidden="true"><img src="/assets/brand/orca-mark.png" alt="" /></span>
      {variant !== 'icon' && <span className="askb-t">{label}</span>}
    </button>
  );
}
